import { QueryCache, QueryClient } from '@tanstack/react-query'

import { ApiError } from './http'
import { identityKeys } from './identity'

/**
 * A 401 from anywhere means this browser is nobody, and says so.
 *
 * Without this, a session revoked while somebody is reading stays invisible:
 * identity is cached for thirty seconds and is not refetched on focus, so the
 * brief's own read answers 401 and renders as error text while the sidebar goes
 * on showing the person it last saw. A member with a session Scout will not
 * accept is not a member, and the gate on `/app` is waiting to be told.
 *
 * Written in rather than invalidated, for the reason signing out already
 * discovered: an observer mounted over a query that is merely invalidated goes
 * on reporting the last thing it saw until a refetch replaces it. Writing the
 * answer notifies every `useMe` at once.
 *
 * This cannot loop. `useMe` swallows its own 401 inside its query function and
 * returns null, so the read that answers "nobody" never surfaces here as an
 * error. A browser holding no anonymous brief is told so with a 404, so the
 * anonymous side is untouched.
 */
const queryCache = new QueryCache({
  onError: (error) => {
    if (error instanceof ApiError && error.status === 401) {
      queryClient.setQueryData(identityKeys.me, null)
    }
  },
})

/**
 * The application's query client.
 *
 * Lives here rather than in the root route so that there is a name for it
 * outside the route tree — the cache is process-wide state, and a test driving
 * the real tree has to be able to start from an empty one.
 */
export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      // Scout's data is crawled on a cadence, not live — refetching on every
      // window focus would be noise.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
})
